import React from 'react';
import { Box, BoxProps } from '@components/Box';
import { Button } from '@components/Button';
import { TextPairing } from '@components/TextPairing';
import { Text } from '@components/typography';
import {
  BubblesWrapper,
  Dot,
  Line,
  MainColumn,
  MainTextContainer,
  SquareBubble,
  StyledColumns,
  StyledSection,
} from './RsvpQuestions.styles';
import { guestListData } from '@data-ts/pages/guest-list';
import { useRsvpQuestionsSectionController } from './RsvpQuestions.controller';
import { Elastic } from 'gsap';
import { getRootUrl } from '@utils/urlHelpers.util';

interface RsvpQuestionsProps
  extends Readonly<{
    backgroundColor: BoxProps['backgroundColor'];
  }> {}

const rsvpQuestionsData = guestListData.rsvpQuestions;

const renderBubbles = (bubbles: ReadonlyArray<SquareBubble>, wrapperStyle?: React.CSSProperties) => {
  return (
    <BubblesWrapper style={wrapperStyle} className="bubbles-wrapper">
      {bubbles.map(bubble => (
        <SquareBubble
          key={bubble.id}
          right={bubble.right}
          bottom={bubble.bottom}
          width={bubble.width}
          height={bubble.height}
          blur={bubble.blur}
          left={bubble.left}
          top={bubble.top}
          zIndex={bubble.zIndex}
          className="square-bubble"
        >
          <Line height={bubble.lineHeight} />
          <Dot />
          {bubble.text && (
            <Text color="purple5" variant="dek24" tagName="p">
              {bubble.text}
            </Text>
          )}
        </SquareBubble>
      ))}
    </BubblesWrapper>
  );
};

export const RsvpQuestions: React.FC<RsvpQuestionsProps> = ({ backgroundColor }) => {
  const { sectionRef, mainTextContainerRef } = useRsvpQuestionsSectionController(allBubbles);
  const rsvpSectionId = 'RSVP_QUESTIONS';

  return (
    <StyledSection id={rsvpSectionId} ref={sectionRef} backgroundColor={backgroundColor} paddingTop={[10, 10, 12]}>
      <StyledColumns>
        <MainColumn width="full">
          {renderBubbles(blurredBubbles, { zIndex: -1 })}
          <MainTextContainer ref={mainTextContainerRef}>
            <Box style={{ maxWidth: '748px' }} marginTop={[7]}>
              <TextPairing
                alignment="center"
                eyebrow={rsvpQuestionsData.eyebrow}
                eyebrowColor="purple4"
                eyebrowVariant="sectionEyeBrow24"
                eyebrowTagName="h2"
                title={rsvpQuestionsData.title}
                titleColor="purple5"
                titleVariant="hed72Hero"
                body={rsvpQuestionsData.content}
                bodyVariant="dek24"
                bodyMaxWidth="390px"
                bodyColor="purple5"
              />
            </Box>
            <Box marginTop={[9]}>
              <Button id="rsvpQuestions" variant="secondary" forwardedAs="a" href={getRootUrl('/')}>
                {rsvpQuestionsData.learnMore}
              </Button>
            </Box>
          </MainTextContainer>
          {renderBubbles(inFocusBubbles)}
        </MainColumn>
      </StyledColumns>
    </StyledSection>
  );
};

export interface SquareBubble
  extends Readonly<{
    id: number;
    left?: string;
    zIndex?: number;
    ease?: gsap.EaseFunction;
    bottom?: string;
    top?: string;
    text?: string;
    lineHeight: string;
    blur: boolean;
    right?: string;
    width?: string;
    height?: string;
  }> {}

const allBubbles: ReadonlyArray<SquareBubble> = [
  {
    id: 1,
    left: '0px',
    zIndex: 2,
    bottom: '5%',
    text: rsvpQuestionsData.joinUsForLunch,
    lineHeight: '465px',
    blur: false,
    ease: Elastic.easeOut.config(1, 0.325),
  },
  {
    id: 2,
    left: '5%',
    bottom: '45%',
    lineHeight: '465px',
    blur: true,
    width: '227px',
    height: '122px',
    zIndex: -1,
    ease: Elastic.easeOut.config(1, 0.385),
  },
  {
    id: 3,
    left: '20%',
    bottom: '30%',
    text: rsvpQuestionsData.whereAreYouStaying,
    lineHeight: '338px',
    blur: false,
    ease: Elastic.easeOut.config(1, 0.375),
  },
  {
    id: 4,
    left: '40%',
    zIndex: -1,
    bottom: '25%',
    lineHeight: '420px',
    width: '179px',
    height: '97px',
    blur: true,
    ease: Elastic.easeOut.config(1, 0.365),
  },
  {
    id: 5,
    left: '40%',
    bottom: '77%',
    text: rsvpQuestionsData.willyouAttend,
    lineHeight: '103px',
    blur: false,
    ease: Elastic.easeOut.config(1, 0.4),
  },
  {
    id: 6,
    bottom: '16%',
    right: '20%',
    text: rsvpQuestionsData.anyAdvice,
    lineHeight: '412px',
    blur: false,
    ease: Elastic.easeOut.config(1, 0.35),
  },
  {
    id: 7,
    bottom: '60%',
    right: '17%',
    lineHeight: '412px',
    blur: true,
    width: '179px',
    height: '92px',
    zIndex: -1,
    ease: Elastic.easeOut.config(1, 0.4),
  },
  {
    id: 8,
    zIndex: 3,
    right: '0',
    top: '78%',
    text: rsvpQuestionsData.anyAllergies,
    lineHeight: '541px',
    blur: false,
    ease: Elastic.easeOut.config(1, 0.3),
  },
  {
    id: 9,
    zIndex: 3,
    bottom: '46%%',
    right: '-5%',
    text: rsvpQuestionsData.needABabySitter,
    lineHeight: '150px',
    blur: false,
    width: '227px',
    height: '122px',
    ease: Elastic.easeOut.config(1, 0.4),
  },
];
const inFocusBubbles = allBubbles.filter(bubble => !bubble.blur) as ReadonlyArray<SquareBubble>;
const blurredBubbles = allBubbles.filter(bubble => bubble.blur) as ReadonlyArray<SquareBubble>;
